import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { authSelector } from '../reducers/index.js'
import 'ag-grid-enterprise'
import { AlertDismissable } from '@hr/common/AlertDismissable.tsx'
import { hrEsalary } from '@hr/main.jsx'
import { useQuery } from '@tanstack/react-query'
import { getLocaleText } from '../actions/datagrid'
import { eSalaryActivateHRUser, eSalaryGetUsersQuery, eSalaryUpdateUser, eSalaryUserPost } from '../api'
import { Button } from '../common/Button.tsx'
import Chip from '../common/chip/Chip'
import { gridSettings, sideBar, statusBar } from '../common/grid'
import { IconAddNew, pencilIconString } from '../common/icons'
import { normalizeUser } from '../common/utils.jsx'
import EditUserModal from './editusermodal'

export default function UserManagement() {
	const { t } = useTranslation()
	const gridRef = useRef()
	const [selectedUser, setSelectedUser] = useState(null)
	const [showModal, setShowModal] = useState(false)
	const [alertStatus, setAlertStatus] = useState({})
	const { fullName, companyName } = useSelector(authSelector)
	const { data: users, refetch: refetchUsers, error } = useQuery(eSalaryGetUsersQuery)

	useEffect(() => {
		if (error) {
			setAlertStatus({
				visible: true,
				severity: 'error',
				message: `${t('UserFetchError', 'Käyttäjätietojen haku epäonnistui')}: ${error.message}`, // error.name
			})
		}
	}, [error, t])

	const roleMap = {
		ADMIN: 'Admin',
		SUPERVISOR: 'Supervisor',
		HRUSER: 'HRUser', // Lesser ADMIN
		CLERK: 'SalaryUser', // Payroll clerk
		EQUIPMENT: 'EquipmentManager',
		EMPLOYEE: 'Employee', // User portal
		//SYSTEM
	}

	const handleOpenModal = useCallback((user) => {
		setSelectedUser(normalizeUser(user))
		setShowModal(true)
	}, [])

	const handleCloseModal = () => {
		setShowModal(false)
		setSelectedUser(null)
	}

	const handleSaveUser = async (userData) => {
		try {
			const userDataForPost = {
				...userData,
				usercode: userData.user,
				username: userData.user,
			}

			delete userDataForPost.user

			userDataForPost.id ? await eSalaryUpdateUser(userDataForPost) : await eSalaryUserPost(userDataForPost)
			await refetchUsers()
		} catch (err) {
			console.error('Virhe käyttäjän tallennuksessa', err)
		}
	}

	const handleActivateHRUser = async (user) => {
		try {
			if (!user.email) {
				setAlertStatus({
					visible: true,
					severity: 'warning',
					message: t('UserHasNoEmail', 'Käyttäjällä ei ole sähköpostiosoitetta'),
				})
				return
			}

			await eSalaryActivateHRUser({
				email: user.email,
				username: user.user,
				name: user.fullname,
				inviter: fullName,
				organization: companyName,
				supportContact: 'tuki@heeros.com',
			})

			refetchUsers()

			setAlertStatus({
				visible: true,
				severity: 'success',
				message: `${t('CredentialsSent', 'Tunnus lähetetty käyttäjälle')} ${user.fullname}`,
			})
		} catch (err) {
			console.error('email sending failed', err)
			setAlertStatus({
				visible: true,
				severity: 'error',
				message: `${t('EmailSendFailed', 'Sähköpostin lähetys epäonnistui')}: ${err.message}`,
			})
		}
	}

	const roleStyles = {
		Admin: { solid: true, bg: '#5952E0', color: '#FFFFFF' },
		HRUser: { solid: false, border: '#5952E0', color: '#5952E0' },
		Supervisor: { solid: true, bg: '#1489B8', color: '#FFFFFF' },
		EquipmentManager: { solid: false, border: '#1489B8', color: '#1489B8' },
		Employee: { solid: true, bg: '#0A60C2', color: '#FFFFFF' },
		SalaryUser: { solid: false, border: '#0A60C2', color: '#0A60C2' },
	}

	const getRoleKey = (row) => {
		return roleMap[row?.role] || null
	}

	const getBadge = (type, value, rowData) => {
		if (type === 'role') {
			const roleKey = getRoleKey(rowData)
			if (!roleKey) return null

			const labels = {
				Admin: t('RoleAdmin', 'Pääkäyttäjä'),
				Supervisor: t('RoleSupervisor', 'Esihenkilö'),
				HRUser: t('RoleHRUser', 'HR käyttäjä'),
				SalaryUser: t('RolePayrollClerk', 'Palkanlaskija'),
				EquipmentManager: t('RoleEquipmentManagement', 'Laitehallinta'),
				Employee: t('RoleEmployee', 'Työntekijä'),
			}

			let text = labels[roleKey]

			if (rowData.customrights) {
				text += ' *'
			}

			const style = roleStyles[roleKey]

			return (
				<Chip
					title={rowData.customrights ? t('CustomRightsTooltip', 'Henkilökohtaisesti muokatut oikeudet') : ''}
					style={{
						backgroundColor: style.solid ? style.bg : 'transparent',
						color: style.color,
						border: style.solid ? 'none' : `1px solid ${style.border}`,
					}}
					data-testid={`role-chip-${roleKey.toLowerCase()}`}
				>
					{text}
				</Chip>
			)
		}

		if (type === 'employment') {
			let variant = 'default'
			let text = value

			switch (value) {
				case 1:
					text = t('Active', 'Aktiivinen')
					variant = 'success'
					break
				case 2:
					text = t('Deactivated', 'Deaktivoitu')
					variant = 'default'
					break
				case 3:
					text = t('OnHold', 'Tauolla')
					variant = 'warning'
					break
				default:
					break
			}

			return (
				<Chip variant={variant} data-testid={`employment-chip-${value}`}>
					{text}
				</Chip>
			)
		}
	}

	const columnDefs = [
		{
			headerName: t('OpenForViewing', 'Avaa tarkasteltavaksi'),
			cellRenderer: (params) => (
				<Button
					testId="Avaa"
					variant="primary"
					size="sm"
					onClick={() => handleOpenModal(params.data)}
					style={{
						color: 'rgb(111, 66, 193)',
						backgroundColor: '#FFF',
						fontWeight: 'bold',
						boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
						border: 'medium',
						padding: '2px 8px',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						position: 'absolute',
						top: '14%',
						left: '14%',
						width: '115px',
						height: '32px',
						fontSize: '0.85rem',
					}}
				>
					{t('OpenForEdit', 'Avaa')}
				</Button>
			),
			pinned: 'left',
		},
		{
			headerName: t('Name', 'Nimi'),
			field: 'fullname',
			filter: 'agTextColumnFilter',
			valueGetter: ({ data }) => data.fullname || data.user,
		},
		{
			headerName: t('Number', 'Numero'),
			field: 'salarynumber',
			width: 100,
		},
		{
			headerName: t('SalaryList', 'Palkkalistoilla'),
			field: 'onpayroll',
			width: 150,
			cellRenderer: (params) => (
				<Form.Check type="checkbox" checked={params.value} readOnly style={{ marginTop: '15px' }} />
			),
		},
		{
			headerName: t('Email', 'Sähköpostiosoite'),
			field: 'email',
			flex: 1,
		},
		{
			headerName: t('Role', 'Rooli'),
			filter: 'agSetColumnFilter',
			valueGetter: (params) => getRoleKey(params.data),
			cellRenderer: (params) => getBadge('role', params.value, params.data),
			filterParams: {
				values: ['Admin', 'Supervisor', 'HRUser', 'SalaryUser', 'EquipmentManager', 'Employee'],
				keyCreator: (params) => params.value,
			},
			width: 150,
		},
		{
			headerName: t('EmploymentStatus', 'Työsuhteen tila'),
			field: 'employmentstatus',
			cellRenderer: (params) => getBadge('employment', params.value, params.data),
			filter: 'agSetColumnFilter',
			filterParams: {
				values: [1, 2, 3],
				keyCreator: (params) => String(params.value),
			},
			width: 150,
		},
		{
			headerName: t('Actions', 'Toimenpiteet'),
			cellRenderer: ({ data }) => {
				const hasEmail = !!data?.email
				const hasRole = !!data?.role

				const disabled = !hasEmail || !hasRole
				const isInvitationSent = data.cognitouser

				return (
					<span style={{ display: 'inline-block', width: '100%' }}>
						<Button
							variant="outline-primary"
							size="sm"
							disabled={disabled}
							style={{
								color: 'rgb(111, 66, 193)',
								backgroundColor: '#FFF',
								fontWeight: 700,
								boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
								padding: '2px 8px',
								position: 'absolute',
								top: '14%',
								left: '10%',
								width: '143px',
								height: '32px',
								fontSize: '0.85rem',
							}}
							onClick={() => handleActivateHRUser(data)}
						>
							{isInvitationSent ? t('UsernameSent', 'Tunnus lähetetty') : t('SendUserName', 'Lähetä tunnus')}
						</Button>
					</span>
				)
			},

			width: 180,

			tooltipValueGetter: ({ data }) => {
				const hasEmail = !!data?.email
				const hasRole = !!data?.role

				const disabled = !hasEmail || !hasRole
				const isInvitationSent = data.cognitouser

				if (isInvitationSent) {
					return t('UsernameResend', 'Lähetä tunnus uudelleen')
				}

				if (!disabled) return null

				if (!hasEmail && !hasRole) {
					return t('MissingEmailAndRole', 'Tunnuksen lähettäminen vaatii sähköpostiosoitteen ja roolin')
				}

				if (!hasEmail) {
					return t('MissingEmail', 'Henkilöltä puuttuu sähköpostiosoite. Lisää sähköpostiosoite henkilötietoihin')
				}

				if (!hasRole) {
					return t('MissingRole', 'Tunnuksen lähettäminen vaatii roolin')
				}

				return null
			},
			tooltipShowDelay: 0,
		},
	]

	const getContextMenuItems = useCallback(
		(params) => {
			const items = []

			items.push({
				name: t('Edit', 'Muokkaa'),
				icon: pencilIconString,
				action: () => handleOpenModal(params.node.data),
			})

			items.push('separator')
			items.push('export')

			return items
		},
		[t, handleOpenModal],
	)

	return (
		<>
			{alertStatus.visible && <AlertDismissable status={alertStatus} setStatus={setAlertStatus} />}
			<Row className="my-1">
				<Col>
					<h2 className="viewheading">{t('UserManagement', 'Käyttäjähallinta')}</h2>
				</Col>
				{location.hostname !== hrEsalary && (
					<Col align="right">
						<Button
							testId="add-external-user"
							className="addbutton"
							variant="primary"
							onClick={() => handleOpenModal({})}
						>
							<IconAddNew /> {t('AddExternalUser', 'Lisää ulkopuolinen käyttäjä')}
						</Button>
					</Col>
				)}
			</Row>

			<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					columnDefs={columnDefs}
					defaultColDef={{ resizable: true, sortable: true, filter: true }}
					sideBar={sideBar}
					rowData={users}
					statusBar={statusBar}
					getLocaleText={(params) => getLocaleText(t, params)}
					getContextMenuItems={getContextMenuItems}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					debug={import.meta.env.DEV}
				/>
			</Row>

			{selectedUser && (
				<EditUserModal show={showModal} onHide={handleCloseModal} user={selectedUser} onSave={handleSaveUser} />
			)}
		</>
	)
}
